import React, { useState } from 'react'
import { MarketplaceDisplay } from '../models/custom-models';
import { useErrorDisplay } from '../components/ErrorDisplay';
import { Box, Button, DialogContent, Radio, TextField, Typography, useTheme } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { I18N } from '../I18N';
import BusySpinner from '../components/BusySpinner';
import I18NText from '../components/I18NText';
import IconButtonEx, { IconButtonExProps } from '../components/IconButtonEx';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MarketplaceClient, { InstallTemplateContext, MarketplaceErrors } from './MarketplaceClient';
import ConditionalDisplay from '../components/ConditionalDisplay';
import { IsEmpty, IsNotEmpty } from '../Helpers';
import PositionAutocompleteControl from '../components/PositionAutocompleteControl';
import MobileEntityCard from '../components/responsive/MobileEntityCard';
import ResponsiveCollection from '../components/responsive/ResponsiveCollection';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../components/responsive';
import useViewportMode from '../hooks/useViewportMode';

export interface MarketplaceTemplateListProps extends Omit<IconButtonExProps, 'onClick'> {
    onAdd?: (id: string) => Promise<void> | void;
    icon?: JSX.Element;
}

const MarketplaceTemplateList = ({ onAdd = async () => { }, icon = <ManageSearchIcon fontSize='small' />, ...btnProps }: MarketplaceTemplateListProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>();
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();

    const [marketplaceTemplates, setMarketplaceTemplates] = useState<MarketplaceDisplay[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<MarketplaceDisplay>(null);
    const [installContext, setInstallContext] = useState<InstallTemplateContext>({ MarketplaceTemplateId: '', PositionId: '', TemplateName: '' });

    const title = 'Browse Marketplace Templates';
    const disableSave = false;

    async function loadData() {
        try {
            setBusy(true);

            const templates = await new MarketplaceClient().GetTemplatesFiltered();
            setMarketplaceTemplates(templates);

        } catch (error) {
            setError(error, MarketplaceErrors.GetAll)
        } finally {
            setBusy(false);
        }
    };

    const handleOpen = async (toggle: boolean) => {
        setOpen(toggle);
        loadData();
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);
            const id = await new MarketplaceClient().Install(installContext);
            await onAdd(id);

            setOpen(false);
        } catch (error) {
            setError(error, MarketplaceErrors.Install)
        } finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setInstallContext({ ...installContext, [name]: value })
    };

    const handleUserSelect = (positionId) => {
        setInstallContext({ ...installContext, PositionId: positionId })
    }

    const handleRowClicked = (ids: string[]) => {
        const selected = marketplaceTemplates.find(x => x.Id === ids[0]);
        if (!selected)
            return;
        setInstallContext({ ...installContext, MarketplaceTemplateId: selected.Id })
        setSelectedTemplate(selected);
    }

    const renderTemplateCard = (template: MarketplaceDisplay) => (
        <MobileEntityCard
            ariaLabel={`Marketplace template: ${template.Name}`}
            title={template.Name}
            subtitle={template.ShortDescription}
            status={<Radio
                checked={selectedTemplate?.Id === template.Id}
                onChange={() => handleRowClicked([template.Id])}
                inputProps={{ 'aria-label': `Select marketplace template: ${template.Name}` }}
                sx={{ width: 44, height: 44 }}
            />}
            fields={[
                { label: I18N.Translate('Tags'), value: template.Tags || '—' },
                { label: I18N.Translate('Price'), value: template.Price ?? '—' },
            ]}
        />
    );

    const desktopGrid = (
        <div style={{ height: 500 }}>
            <DataGridPro
                density="compact"
                onSelectionModelChange={handleRowClicked}
                checkboxSelection
                disableMultipleSelection
                components={{ Toolbar: GridToolbar }}
                rows={marketplaceTemplates.map(x => { return { ...x, id: x.Id } })}
                columns={[
                    { field: 'Name', headerName: I18N.Translate('Template Name'), flex: 4, editable: false },
                    { field: 'ShortDescription', headerName: I18N.Translate('Short Description'), flex: 4, editable: false },
                    { field: 'Tags', headerName: I18N.Translate('Tags'), flex: 3, editable: false },
                    { field: 'Price', headerName: I18N.Translate('Price'), flex: 1, editable: false, type: 'number' },
                ]}
            />
        </div>
    );

    return (
    <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} onClick={() => handleOpen(true)} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth
            maxWidth="md"
            tabletFullScreen="portrait"
            PaperProps={{ sx: { '& > .responsive-dialog-busy': { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } } }}
        >
            <BusySpinner busy={busy} className="responsive-dialog-busy">
                <ResponsiveDialogHeader id="customized-dialog-title" title={title} onClose={() => handleOpen(false)} />
                <DialogContent dividers sx={{ p: viewport.isDesktop ? theme.spacing(2) : 0, minHeight: 0, overflowY: 'auto' }}>
                    <ResponsiveCollection
                        viewMode={viewport.mode}
                        items={marketplaceTemplates}
                        getKey={template => template.Id}
                        renderCard={renderTemplateCard}
                        desktop={desktopGrid}
                        ariaLabel="Marketplace templates"
                        emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No marketplace templates found</Typography>}
                    />
                    <ConditionalDisplay ifTrue={IsNotEmpty(selectedTemplate)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', m: theme.spacing(2) }}>
                            <TextField label="Template Name" fullWidth
                                name="TemplateName"
                                size="small"
                                variant="outlined"
                                error={IsEmpty(installContext.TemplateName)}
                                value={installContext.TemplateName}
                                onChange={handleInputChange} />
                            <PositionAutocompleteControl positionId={installContext.PositionId} onUserSelect={handleUserSelect} />
                        </Box>
                    </ConditionalDisplay>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button onClick={() => handleOpen(false)} variant='text' color='inherit'><I18NText>Cancel</I18NText></Button>
                    <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'><I18NText>Create Template</I18NText></Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>
    );
}

export default MarketplaceTemplateList
